import { AnchorButton } from "@svea/design-system-components-react";
import { QuestionSet, useText } from "@sveabank/cc-common";
import { Link } from "react-router";
import Content from "../../components/content";
import PageNavigationFooter from "../../components/page-navigation-footer";
import { withLoader } from "../../hoc/with-loader";
import { usePageContext } from "../../hooks/context";
import { usePages } from "../../hooks/usePages";
import { useKyc } from "./hooks";
import AmlQuestions from "./steps/aml-questions";
import ApplicationQuestions from "./steps/application-questions";
import BeneficialOwnership from "./steps/beneficial-ownership";
import BeneficialOwnerQuestions from "./steps/bo-questions";
import SniCodes from "./steps/sni-code";
import { getProductTitle, hasCompletedAmlQuestionsForProduct } from "./utils";

const KycPage = () => {
	const [t, texts] = useText();
	const {
		amlQuestions,
		applicationQuestions,
		products,
		steps,
		submitAmlQuestions,
		submitApplicationQuestions,
		confirmBeneficialOwners,
		hasCompletedBeneficialOwner,
		hasCompletedBoQuestionsStep,
		submitBeneficialOwnerQuestions,
		hasCompletedApplicationQuestions,
		shouldDisplayBeneficialOwner,
		shouldDisplaySniCode,
		hasCompletedSniCode,
		assignSniCodes,
		shouldDisplayBeneficialOwnerQuestions,
		allCountries,
	} = usePageContext(useKyc);

	const { nextPage, prevPage, nextPageAvailable } = usePages();

	return (
		<Content my={8}>
			<QuestionSet
				questions={[
					...(shouldDisplaySniCode
						? [
								{
									component: <SniCodes onSubmit={assignSniCodes} />,
									heading: t(texts.kyc.sniCode.heading),
									description: t(texts.kyc.sniCode.description),
									isCompleted: hasCompletedSniCode,
								},
							]
						: []),
					{
						component: (
							<ApplicationQuestions onSubmit={submitApplicationQuestions(applicationQuestions)} />
						),
						heading: t(texts.kyc.applicationQuestions.heading),
						description: t(texts.kyc.applicationQuestions.description),
						isCompleted: hasCompletedApplicationQuestions,
					},
					...amlQuestions
						.slice()
						.sort((a, b) => {
							if (a.productDefinitionId < b.productDefinitionId) {
								return -1;
							}
							if (a.productDefinitionId > b.productDefinitionId) {
								return 1;
							}
							return 0;
						})
						.map(({ questions, productId, productDefinitionId }) => ({
							component: (
								<AmlQuestions
									questions={questions}
									productId={productId}
									onSubmit={submitAmlQuestions({
										questions,
										productId,
										productDefinitionId,
									})}
									allCountries={allCountries}
								/>
							),
							description: t(texts.kyc.amlQuestions.description),
							heading: t(getProductTitle(productId, products)),
							isCompleted: hasCompletedAmlQuestionsForProduct(steps, productId),
						})),
					...(shouldDisplayBeneficialOwner
						? [
								{
									component: <BeneficialOwnership onSubmit={confirmBeneficialOwners} />,
									heading: t(texts.kyc.beneficialOwnership.title),
									description: t(texts.kyc.beneficialOwnership.description),
									isCompleted: hasCompletedBeneficialOwner,
								},
							]
						: []),
					...(shouldDisplayBeneficialOwnerQuestions
						? [
								{
									component: <BeneficialOwnerQuestions onSubmit={submitBeneficialOwnerQuestions} />,
									heading: t(texts.kyc.beneficialOwnerQuestions.heading),
									description: t(texts.kyc.beneficialOwnerQuestions.description),
									isCompleted: hasCompletedBoQuestionsStep,
								},
							]
						: []),
				]}
			/>

			<PageNavigationFooter paddingTop="sign">
				{nextPageAvailable && nextPage && (
					<AnchorButton as={Link} to={nextPage}>
						{t(texts.common.continue)}
					</AnchorButton>
				)}
				{prevPage && (
					<AnchorButton as={Link} variant="secondary" to={prevPage}>
						{t(texts.choosePackage.back)}
					</AnchorButton>
				)}
			</PageNavigationFooter>
		</Content>
	);
};

export default withLoader(useKyc, KycPage);
